import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useMatches, useUnits } from '@/features/inventory/useInventory';
import { formatMoney } from '@/lib/format';
import { useCreateReservation } from './useDeals';

interface Props {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadRevision?: string | undefined;
}

/**
 * Opens a reservation: the deal, not the Phase 4 hold.
 *
 * The unit list is every available unit, but it is ordered by this lead's match
 * score so the rep's own shortlist is at the top. Restricting it to matches only
 * would be wrong — a client who walks in and asks for a specific unit is not a
 * data-entry error.
 */
export function CreateReservationDialog({ open, onClose, leadId, leadRevision }: Props) {
  const { t } = useTranslation();
  const createReservation = useCreateReservation();

  const { data: units } = useUnits({ availability: 'available' });
  const { data: matches } = useMatches(open ? leadId : undefined, leadRevision);

  const [unitId, setUnitId] = useState('');
  const [holdDays, setHoldDays] = useState('14');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Units this lead already holds from a Phase 4 hold are not in the "available"
  // list, so they are added back: create_reservation() accepts them, and leaving
  // them out would make the held unit the one thing you cannot book.
  const options = useMemo(() => {
    const score = new Map((matches ?? []).map((m) => [m.unit_id, m.score]));

    const fromUnits = (units ?? []).map((u) => ({
      id: u.id,
      label: `${u.projects?.name ?? ''} · ${u.unit_code} · ${formatMoney(u.price_total)}`,
      score: score.get(u.id) ?? -1,
    }));

    const known = new Set(fromUnits.map((u) => u.id));
    const held = (matches ?? [])
      .filter((m) => !known.has(m.unit_id))
      .map((m) => ({
        id: m.unit_id,
        label: `${m.project_name} · ${m.unit_code} · ${formatMoney(m.price_total)}`,
        score: m.score,
      }));

    return [...fromUnits, ...held].sort((a, b) => b.score - a.score);
  }, [units, matches]);

  useEffect(() => {
    if (!open) return;
    setUnitId('');
    setHoldDays('14');
    setNotes('');
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!unitId) {
      setError(t('deals.chooseUnitFirst'));
      return;
    }
    try {
      await createReservation.mutateAsync({
        leadId,
        unitId,
        holdDays: Number(holdDays) || 14,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('deals.createReservation')} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <p className="text-xs text-slate-500">{t('deals.reservationIntro')}</p>

        <Select
          label={t('deals.unit')}
          placeholder={t('deals.chooseUnit')}
          value={unitId}
          onChange={(e) => setUnitId(e.target.value)}
          options={options.map((o) => ({ value: o.id, label: o.label }))}
        />

        <Input
          label={t('deals.holdDays')}
          hint={t('deals.holdDaysHint')}
          type="number"
          min="1"
          max="180"
          dir="ltr"
          value={holdDays}
          onChange={(e) => setHoldDays(e.target.value)}
        />

        <Input label={t('deals.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={createReservation.isPending} onClick={() => void submit()}>
            {t('deals.createReservation')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
